const MAX_THUMBNAIL_DIMENSION = 1920;
const DEFAULT_CAPTURE_TIME_SECONDS = 1;
const CAPTURE_STEP_SECONDS = 1;
const MAX_CAPTURE_CANDIDATES = 5;
const DARK_FRAME_MEAN_LUMINANCE = 24;
const FALLBACK_THUMBNAIL_NAME = "thumbnail";

export interface FrameDimensions {
  width: number;
  height: number;
}

// Frame 0 is often black or mid fade-in, so capture starts a second in and steps forward past dark frames.
export const getCaptureTimes = (duration: number): number[] => {
  if (!Number.isFinite(duration)) {
    return [DEFAULT_CAPTURE_TIME_SECONDS];
  }

  const start = Math.min(DEFAULT_CAPTURE_TIME_SECONDS, duration / 2);
  const times = [start];
  for (
    let time = start + CAPTURE_STEP_SECONDS;
    times.length < MAX_CAPTURE_CANDIDATES && time < duration;
    time += CAPTURE_STEP_SECONDS
  ) {
    times.push(time);
  }

  return times;
};

export const getThumbnailDimensions = ({ width, height }: FrameDimensions): FrameDimensions => {
  const longSide = Math.max(width, height);
  if (longSide <= MAX_THUMBNAIL_DIMENSION) {
    return { width, height };
  }

  const scale = MAX_THUMBNAIL_DIMENSION / longSide;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
};

export const isFrameTooDark = (rgbaPixels: ArrayLike<number>): boolean => {
  const pixelCount = Math.floor(rgbaPixels.length / 4);
  if (pixelCount === 0) {
    return true;
  }

  let luminanceSum = 0;
  for (let i = 0; i < pixelCount * 4; i += 4) {
    luminanceSum += ((rgbaPixels[i] ?? 0) + (rgbaPixels[i + 1] ?? 0) + (rgbaPixels[i + 2] ?? 0)) / 3;
  }

  return luminanceSum / pixelCount < DARK_FRAME_MEAN_LUMINANCE;
};

export const getThumbnailFileName = (videoFileName: string): string => {
  const dotIndex = videoFileName.lastIndexOf(".");
  const baseName = dotIndex >= 0 ? videoFileName.slice(0, dotIndex) : videoFileName;
  return `${baseName || FALLBACK_THUMBNAIL_NAME}.jpg`;
};
